/**
 * Frontend configuration.
 *
 * ⚠ Everything in here is PUBLIC. Vite inlines `VITE_*` values into the bundle
 * at build time, so anyone can read them with dev tools. That is fine for the
 * two below — the publishable key is public by design and Row Level Security is
 * what protects the data — but it means no secret may ever be added here.
 * Server-side secrets live in Key Vault, behind the Azure Functions.
 */

function required(name: string): string {
  const value = import.meta.env[name as keyof ImportMetaEnv] as string | undefined;
  if (value === undefined || value.trim() === '') {
    throw new Error(
      `Missing build-time variable: ${name}. Add it to apps/web/.env (copy .env.example), ` +
        `or to the CI build environment. Vite only inlines names beginning VITE_.`,
    );
  }
  return value;
}

export const supabaseUrl = (): string => required('VITE_SUPABASE_URL');
export const supabasePublishableKey = (): string => required('VITE_SUPABASE_PUBLISHABLE_KEY');

/**
 * The release this bundle was built from. The deploy workflow sets
 * `VITE_APP_VERSION` to the tag (`0.1.0`) or the dev build (`0.1.0-dev.abc1234`);
 * a local build has none and says so.
 */
export function appVersion(): string {
  const v = import.meta.env.VITE_APP_VERSION as string | undefined;
  return v?.trim() ? v.trim() : 'local';
}

/**
 * Which environment this bundle was built for: the deploy sets `VITE_APP_ENV` to the GitHub environment it deploys
 * (`dev`, `prod`); a local build has none and is `local` — the same as anything not `dev` or `prod`.
 */
export function environmentOf(value: string | undefined): 'dev' | 'prod' | 'local' {
  const v = value?.trim().toLowerCase();
  return v === 'dev' || v === 'prod' ? v : 'local';
}

/** This bundle's environment (`environmentOf`); only `dev` draws the rail's badge (AppShell). */
export function appEnvironment(): 'dev' | 'prod' | 'local' {
  return environmentOf(import.meta.env.VITE_APP_ENV as string | undefined);
}

/** Present without reading values — enough to render a useful config banner. */
export function configPresence(): Record<string, boolean> {
  const names = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_PUBLISHABLE_KEY'] as const;
  return Object.fromEntries(
    names.map((n) => {
      const v = import.meta.env[n as keyof ImportMetaEnv] as string | undefined;
      return [n, typeof v === 'string' && v.trim() !== ''];
    }),
  );
}
